(ns practitest.frontend.components2.dashboard.charts.tooltip
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.common :as c]))

(defn- tooltip-bg-path-d
  [w h r aw ah arrow-cx]
  ;; arrow-cx is the center of the arrow relative to the tooltip's left edge.
  ;; Clamp it so the arrow stays within the rounded corners.
  (let [half-aw  (/ aw 2)
        arrow-cx (-> arrow-cx
                     (max (+ r half-aw))
                     (min (- w r half-aw)))]
    (str "M " r ",0 "
         "H " (- w r) " "
         "A " r "," r " 0 0,1 " w "," r " "
         "V " (- h r) " "
         "A " r "," r " 0 0,1 " (- w r) "," h " "
         "H " (+ arrow-cx half-aw) " "
         "L " arrow-cx "," (+ h ah) " "
         "L " (- arrow-cx half-aw) "," h " "
         "H " r " "
         "A " r "," r " 0 0,1 " 0 "," (- h r) " "
         "V " r " "
         "A " r "," r " 0 0,1 " r ",0 Z")))

(defn- clamp-x [x tooltip-w chart-width]
  (-> x (max 0) (min (- chart-width tooltip-w))))

(defn- ensure-drop-shadow-filter! [svg]
  (let [defs (c/ensure-el! svg "defs" "pt-chart-defs")]
    (when (.empty (.select defs "#tooltip-shadow"))
      (-> defs
          (.append "filter")
          (.attr "id" "tooltip-shadow")
          (.attr "x" "-20%") (.attr "y" "-20%")
          (.attr "width" "140%") (.attr "height" "140%")
          (.append "feDropShadow")
          (.attr "dx" "0") (.attr "dy" "0")
          (.attr "stdDeviation" "5")
          (.attr "flood-color" "var(--pt-color-border-default)")))))

(defn setup-tooltip!
  "Append <g class='tooltip'> to g-chart (idempotent).
   Also injects a drop-shadow filter into the root <svg>.
   Returns the tooltip <g> DOM node."
  [g-chart]
  (let [svg-node (.. (.node g-chart) -ownerSVGElement)
        svg      (.select d3 svg-node)]
    (.attr svg "overflow" "visible")
    (ensure-drop-shadow-filter! svg))
  (let [existing (.select g-chart "g.chart-tooltip")]
    (if (.empty existing)
      (let [g (-> g-chart
                  (.append "g")
                  (.attr "class" "chart-tooltip")
                  (.attr "visibility" "hidden")
                  (.attr "pointer-events" "none"))]
        (-> g (.append "path") (.attr "class" "tooltip-bg"))
        (-> g (.append "g")    (.attr "class" "tooltip-content"))
        (.node g))
      (.node existing))))

(defn hide-tooltip! [tooltip-g]
  (-> (.select d3 tooltip-g)
      (.attr "visibility" "hidden")))

(defn show-tooltip!
  "Populate and position the tooltip above anchor-x/anchor-y.
   data = {:category str :rows [{:label :value :color}] :anchor-x num :anchor-y num}"
  [tooltip-g {:keys [category rows anchor-x anchor-y]} chart-width]
  (let [pad     10
        row-h   18
        title-h 22
        dot-r   4
        aw      12
        ah      10
        sel     (.select d3 tooltip-g)
        content (.select sel "g.tooltip-content")]

    (-> content (.selectAll "*") (.remove))

    (-> content
        (.append "text")
        (.attr "class" "tooltip-title")
        (.attr "x" pad)
        (.attr "y" (+ pad 14))
        (.attr "font-weight" "bold")
        (.attr "font-size" "12px")
        (.attr "fill" "#333")
        (.text category))

    (dorun
      (map-indexed
       (fn [i {:keys [label value color]}]
         (let [row-y (+ pad title-h (* i row-h))]
           (-> content
               (.append "circle")
               (.attr "class" "tooltip-dot")
               (.attr "cx" (+ pad dot-r))
               (.attr "cy" (+ row-y (/ row-h 2)))
               (.attr "r" dot-r)
               (.attr "fill" color))
           (-> content
               (.append "text")
               (.attr "class" "tooltip-row")
               (.attr "x" (+ pad (* dot-r 2) 4))
               (.attr "y" (+ row-y (/ row-h 2) 4))
               (.attr "fill" color)
               (.attr "font-size" "11px")
               (.text (str label ": " value)))))
       rows))

    (let [bbox  (.getBBox (.node content))
          w     (max 80 (+ (.-width bbox) (* pad 2)))
          h     (+ (.-height bbox) (* pad 2))
          x        (clamp-x (- anchor-x (/ w 2)) w chart-width)
          arrow-cx (- anchor-x x)
          y-above  (- anchor-y h ah 8)
          y        (if (neg? y-above) (+ anchor-y 8) y-above)]

      (-> (.select sel "path.tooltip-bg")
          (.attr "d" (tooltip-bg-path-d w h 10 aw ah arrow-cx))
          (.attr "fill" "white")
          (.attr "stroke" "var(--pt-color-primary)")
          (.attr "stroke-width" "1")
          (.attr "filter" "url(#tooltip-shadow)"))

      (-> sel
          (.attr "transform" (str "translate(" x "," y ")"))
          (.attr "visibility" "visible")))))

(defn make-tooltip-api
  "Returns {:show! fn :hide! fn} for injection into render ctx."
  [tooltip-g chart-width]
  {:show! (fn [data] (show-tooltip! tooltip-g data chart-width))
   :hide! (fn []     (hide-tooltip! tooltip-g))})